import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface MarketingPackage {
  tierName: string;
  price: number;
  originalPrice: number;
  gbpProfiles: number;
  backlinks: number;
  extraBacklinks?: number;
  extraPressReleases?: number;
  seoCredits: number;
  hasSocialPosting: boolean;
  hasRoasTracking: boolean;
  aeoBlogs: string;
  strategySessions: string;
  pressReleases: number;
  features: string[];
  description: string;
  startWhenNewSiteLive?: boolean;
}

export interface WebsitePackage {
  tierName: string;
  pages: number;
  months: number;
  bizDays?: number;
  timelineDisplay?: string;
  investment: number;
  monthly: number;
  features: string[];
  description?: string;
  paymentPlan?: 'full' | '6-month' | '12-month' | '18-month';
  paymentDetails?: {
    fee: number;
    total: number;
    months: number;
    monthly: number;
  };
}

export interface TechFixesPackage {
  type: 'one-time' | 'hosting' | 'hosting-only';
  price: number;
  monthlyPrice?: number;
  description: string;
}

export interface AddOn {
  id: string;
  name: string;
  description: string;
  price: number;
  priceType: 'monthly' | 'one-time';
  features?: string[];
}

export interface ContactInfo {
  businessName: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  salesRep: string;
  customerType: string;
  industryType: string;
  companyType: string;
  referralSource: string;
  accountManager: string;
  strategyToken?: string;
}

export interface AppliedPromo {
  code: string;
  discount_type: 'percentage' | 'flat';
  discount_value: number;
  applies_to: string;
  description?: string;
  discount_amount: number; // calculated dollar amount off
}

interface SOWContextType {
  marketingPackage: MarketingPackage | null;
  websitePackage: WebsitePackage | null;
  techFixesPackage: TechFixesPackage | null;
  addOns: AddOn[];
  contactInfo: ContactInfo;
  appliedPromo: AppliedPromo | null;
  setMarketingPackage: (pkg: MarketingPackage | null) => void;
  setWebsitePackage: (pkg: WebsitePackage | null) => void;
  setTechFixesPackage: (pkg: TechFixesPackage | null) => void;
  addAddOn: (addOn: AddOn) => void;
  removeAddOn: (id: string) => void;
  hasAddOn: (id: string) => boolean;
  setContactInfo: (info: Partial<ContactInfo>) => void;
  setAppliedPromo: (promo: AppliedPromo | null) => void;
  clearAll: () => void;
  hasItems: boolean;
  totalOneTime: number;
  totalMonthly: number;
  promoDiscountAmount: number;
}

const SOWContext = createContext<SOWContextType | undefined>(undefined);

const CONTACT_INFO_STORAGE_KEY = 'sow_contact_info';
const PACKAGES_STORAGE_KEY = 'sow_packages';

const getStoredContactInfo = (): ContactInfo => {
  try {
    const stored = localStorage.getItem(CONTACT_INFO_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error loading contact info from storage:', e);
  }
  return {
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    website: "",
    salesRep: "",
    customerType: "",
    industryType: "",
    companyType: "",
    referralSource: "",
    accountManager: "",
    strategyToken: "",
  };
};

interface StoredPackages {
  marketingPackage: MarketingPackage | null;
  websitePackage: WebsitePackage | null;
  techFixesPackage: TechFixesPackage | null;
  addOns: AddOn[];
}

const getStoredPackages = (): StoredPackages => {
  try {
    const stored = localStorage.getItem(PACKAGES_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error loading packages from storage:', e);
  }
  return {
    marketingPackage: null,
    websitePackage: null,
    techFixesPackage: null,
    addOns: [],
  };
};

export const SOWProvider = ({ children }: { children: ReactNode }) => {
  const storedPackages = getStoredPackages();
  const [marketingPackage, setMarketingPackageState] = useState<MarketingPackage | null>(storedPackages.marketingPackage);
  const [websitePackage, setWebsitePackageState] = useState<WebsitePackage | null>(storedPackages.websitePackage);
  const [techFixesPackage, setTechFixesPackageState] = useState<TechFixesPackage | null>(storedPackages.techFixesPackage);
  const [addOns, setAddOns] = useState<AddOn[]>(storedPackages.addOns);
  const [contactInfo, setContactInfoState] = useState<ContactInfo>(getStoredContactInfo);
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromo | null>(null);

  // Persist packages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(PACKAGES_STORAGE_KEY, JSON.stringify({
        marketingPackage,
        websitePackage,
        techFixesPackage,
        addOns,
      }));
    } catch (e) {
      console.error('Error saving packages to storage:', e);
    }
  }, [marketingPackage, websitePackage, techFixesPackage, addOns]);

  const hasItems = !!(marketingPackage || websitePackage || techFixesPackage || addOns.length > 0);

  const addOnsOneTime = addOns
    .filter(a => a.priceType === 'one-time')
    .reduce((sum, a) => sum + a.price, 0);
  
  const addOnsMonthly = addOns
    .filter(a => a.priceType === 'monthly')
    .reduce((sum, a) => sum + a.price, 0);

  // Calculate promo discount amount
  const promoDiscountAmount = (() => {
    if (!appliedPromo) return 0;
    if (appliedPromo.applies_to === 'website_one_time') {
      const websiteInvestment = websitePackage?.investment || 0;
      if (appliedPromo.discount_type === 'percentage') {
        return Math.round(websiteInvestment * (appliedPromo.discount_value / 100));
      }
      return Math.min(appliedPromo.discount_value, websiteInvestment);
    }
    // For other applies_to types, calculate after totalOneTime is known
    return appliedPromo.discount_amount || 0;
  })();

  const rawOneTime = (websitePackage?.investment || 0) + (techFixesPackage?.price || 0) + addOnsOneTime;
  const totalOneTime = Math.max(0, rawOneTime - promoDiscountAmount);
  
  // If marketing starts when new site goes live, don't include it in immediate monthly total
  const marketingMonthly = (marketingPackage?.startWhenNewSiteLive) ? 0 : (marketingPackage?.price || 0);
  
  const totalMonthly = 
    marketingMonthly + 
    (websitePackage?.monthly || 0) + 
    (techFixesPackage?.monthlyPrice || 0) +
    addOnsMonthly;

  const setMarketingPackage = (pkg: MarketingPackage | null) => {
    setMarketingPackageState(pkg);
  };

  const setWebsitePackage = (pkg: WebsitePackage | null) => {
    setWebsitePackageState(pkg);
  };

  const setTechFixesPackage = (pkg: TechFixesPackage | null) => {
    setTechFixesPackageState(pkg);
  };

  const addAddOn = (addOn: AddOn) => {
    setAddOns(prev => {
      const exists = prev.find(a => a.id === addOn.id);
      if (exists) {
        return prev.map(a => a.id === addOn.id ? addOn : a);
      }
      return [...prev, addOn];
    });
  };

  const removeAddOn = (id: string) => {
    setAddOns(prev => prev.filter(a => a.id !== id));
  };

  const hasAddOn = (id: string) => {
    return addOns.some(a => a.id === id);
  };

  const setContactInfo = (info: Partial<ContactInfo>) => {
    setContactInfoState(prev => {
      const updated = { ...prev, ...info };
      try {
        localStorage.setItem(CONTACT_INFO_STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Error saving contact info to storage:', e);
      }
      return updated;
    });
  };

  const clearAll = () => {
    setMarketingPackageState(null);
    setWebsitePackageState(null);
    setTechFixesPackageState(null);
    setAddOns([]);
    setAppliedPromo(null);
    const emptyInfo = {
      businessName: "",
      contactName: "",
      email: "",
      phone: "",
      website: "",
      salesRep: "",
      customerType: "",
      industryType: "",
      companyType: "",
      referralSource: "",
      accountManager: "",
      strategyToken: "",
    };
    setContactInfoState(emptyInfo);
    try {
      localStorage.removeItem(CONTACT_INFO_STORAGE_KEY);
      localStorage.removeItem(PACKAGES_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing storage:', e);
    }
  };

  return (
    <SOWContext.Provider
      value={{
        marketingPackage,
        websitePackage,
        techFixesPackage,
        addOns,
        contactInfo,
        appliedPromo,
        setMarketingPackage,
        setWebsitePackage,
        setTechFixesPackage,
        addAddOn,
        removeAddOn,
        hasAddOn,
        setContactInfo,
        setAppliedPromo,
        clearAll,
        hasItems,
        totalOneTime,
        totalMonthly,
        promoDiscountAmount,
      }}
    >
      {children}
    </SOWContext.Provider>
  );
};

export const useSOW = () => {
  const context = useContext(SOWContext);
  if (context === undefined) {
    throw new Error('useSOW must be used within a SOWProvider');
  }
  return context;
};